import { useCallback, useMemo, useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Edition } from '../../models/Edition'
import { Item } from '../../models/Item'
import { ItemSubType } from '../../types/models'
import { Result } from '../../types/utils'
import { getIconNameFromType } from '../../utils/item'
import { currencyFormat } from '../../utils/numbers'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Paragraph from '../app/design/Paragraph'
import { ImageView } from '../common/ImageView'
import { ConfirmationModal } from '../common/modals/ConfirmationModal'
import { SelectList } from '../common/SelectList'
import { WorkflowPage } from '../workflows/WorkflowPage'
import { ItemPage } from './ItemPage'

export type PluralItemType =
    | 'services'
    | 'products'
    | 'classes'
    | 'reservations'
    | 'packages'
    | 'subscriptions'
    | 'gifts'

export const itemTitleAndHelperText = (type: ItemSubType) => {
    switch (type) {
        case 'service':
            return {
                title: 'Services',
                helperText:
                    'Here’s a collection of recommended services for your business. Select the ones that you’d like to add to your account and make edits if needed.',
                noItemsText:
                    'There are no recommended services for your selected edition. Please click "Next" to continue.',
            }
        case 'product':
            return {
                title: 'Products',
                helperText: 'Add-on products can be added to any client booking. ',
                noItemsText:
                    'There are no recommended products for your selected edition. Please click "Next" to continue.',
            }
        case 'class':
            return {
                title: 'Classes',
                helperText:
                    'This set of curated classes will help keep you and your clients on a set schedule.',
                noItemsText:
                    'There are no recommended classes for your selected edition. Please click "Next" to continue.',
            }
        case 'reservation':
            return {
                title: 'Reservations',
                helperText:
                    'Sell overnight and multi-day appointments as reservations to keep your other availability open. Here are some that might be useful.',
                noItemsText:
                    'There are no recommended reservations for your selected edition. Please click "Next" to continue.',
            }
        case 'package':
            return {
                title: 'Packages',
                helperText:
                    'Bundle offerings together as “packages” that your customers can buy. We’ve created a few just for you.',
                noItemsText:
                    'There are no recommended packages for your selected edition. Please click "Next" to continue.',
            }
        case 'subscription':
            return {
                title: 'Subscriptions',
                helperText:
                    'The simple way to recurring payments for your business. Select a subscription to add it to your account.',
                noItemsText:
                    'There are no recommended subscriptions for your selected edition. Please click "Next" to continue.',
            }
        case 'gift':
            return {
                title: 'Gift Certificates',
                helperText: 'Gift certificates can be added to any client booking. ',
                noItemsText:
                    'There are no recommended gift certificates for your selected edition. Please click "Next" to continue.',
            }
        default:
            return {
                title: '',
                helperText: '',
                noItemsText: '',
            }
    }
}

const premiumItems = ['package', 'subscription', 'product', 'reservation']

type SelectableItem = {
    item: Item
    selected: boolean
}

type ItemsPageProps = {
    items: SelectableItem[]
    itemType: ItemSubType
    edition?: Edition
    onSave: (items: Item[]) => Promise<Result<any>>
    allowNextWithoutSelection?: boolean
    enableAllFields?: boolean
    onClose: () => void
    onBack?: {
        title?: string
        onClick?: () => void
    }
}

export function ItemsPage({
    items,
    itemType,
    onSave,
    edition,
    enableAllFields = true,
    onClose,
    onBack,
}: ItemsPageProps) {
    const user = PSApplication.me()

    const { logAndDisplayError } = useErrorHandler()
    const [itemToEdit, setItemToEdit] = useState<
        { type: 'simple' | 'full'; item: Item } | undefined
    >(undefined)
    const [selectableItems, setSelectableItems] = useState<SelectableItem[]>(items)
    const [isShowingPremiumWarningModal, setIsShowingPremiumWarningModal] = useState(false)

    const selectableItemListItems = useMemo(() => {
        return selectableItems.map((i) => ({
            id: i.item.id ?? '',
            title: i.item.name ?? '',
            subtitle: currencyFormat(i.item.rate ?? 0),
            tertiaryView: (
                <ImageView
                    fallbackIcon={{
                        icon: `${getIconNameFromType(i.item.type ?? '')}.large`,
                        background: 'round',
                        className: 'text-primary-700',
                    }}
                    className="size-12 rounded-full"
                    image={i.item.image}
                />
            ),
            selected: i.selected,
            button: i.selected
                ? {
                      title: 'Edit',
                      onClick: () => setItemToEdit({ type: 'simple', item: i.item }),
                  }
                : undefined,
        }))
    }, [selectableItems])
    const [isSaving, setIsSaving] = useState(false)

    const onNextHandler = useCallback(async () => {
        // Send the new items with the proper IDs set
        setIsSaving(true)
        const result = await onSave(selectableItems.filter((i) => i.selected).map((i) => i.item))
        setIsSaving(false)
        if (!result.ok) {
            logAndDisplayError(result.error)
        }
    }, [onSave, selectableItems])

    const { title, helperText, noItemsText } = itemTitleAndHelperText(itemType)

    const isPremium = premiumItems.includes(itemType) && edition?.allowBasicPlan

    const handleSelectItems = (items: { id: string }[]) => {
        const previouslySelectedItemLength = selectableItems.filter((i) => i.selected).length
        const selectedItemIDs = items.map((c) => c.id)

        const didSelectNewItem = items.length > previouslySelectedItemLength
        if (
            didSelectNewItem &&
            isPremium &&
            !user.plan &&
            sessionStorage.getItem('viewed_premium_upsell') !== '1'
        ) {
            sessionStorage.setItem('viewed_premium_upsell', '1')
            setIsShowingPremiumWarningModal(true)
        }

        setSelectableItems((s) =>
            s.map((c) => {
                if (selectedItemIDs.includes(c.item.id ?? '')) {
                    return { ...c, selected: true }
                }
                return { ...c, selected: false }
            })
        )
    }

    return (
        <WorkflowPage
            backButtonTitle={onBack?.title}
            onBack={() => {
                if (itemToEdit?.type === 'full') {
                    setItemToEdit({ ...itemToEdit, type: 'simple' })
                }
                else {
                    onBack?.onClick?.()
                }
            }}
            onClose={onClose}
            footerContent={
                <div className="flex items-center justify-end">
                    <Button
                        intent="primary"
                        onClick={onNextHandler}
                        disabled={isSaving}
                        isLoading={isSaving}
                    >
                        Next
                    </Button>
                </div>
            }
        >
            <Heading size="xl" className="mt-[5px]">
                {`${title}${isPremium ? ' (Premium)' : ''}`}
            </Heading>
            <Paragraph className="mt-4 max-w-[580px] text-center text-gray-600">
                {helperText}
            </Paragraph>
            <WorkflowPage.Card className="mt-12">
                {selectableItems.length ? (
                    <SelectList
                        selectionType="multi"
                        items={selectableItemListItems}
                        selectedItems={selectableItemListItems.filter((i) => i.selected)}
                        onSelectItems={handleSelectItems}
                    />
                ) : (
                    <Paragraph size="md" className="text-gray-600">
                        {noItemsText}
                    </Paragraph>
                )}
            </WorkflowPage.Card>
            {itemToEdit?.type === 'simple' && (
                <WorkflowPage.ItemLiteModal
                    isOpen
                    title={`Edit ${
                        itemToEdit.item.type === 'gift' ? 'gift certificate' : itemToEdit.item.type
                    }`}
                    type={itemToEdit.item.type}
                    onClose={() => setItemToEdit(undefined)}
                    item={itemToEdit.item}
                    onDone={(updatedItem) => {
                        setSelectableItems((s) =>
                            s.map((c) =>
                                c.item.id === itemToEdit.item.id ? { ...c, item: updatedItem } : c
                            )
                        )
                        setItemToEdit(undefined)
                    }}
                    onMoreDetails={() => setItemToEdit({ ...itemToEdit, type: 'full' })}
                />
            )}

            {itemToEdit?.type === 'full' && (
                <ItemPage
                    item={itemToEdit.item}
                    itemType={itemType}
                    onUpdateItem={(updatedItem) => {
                        setSelectableItems((s) =>
                            s.map((c) =>
                                c.item.id === itemToEdit.item.id ? { ...c, item: updatedItem } : c
                            )
                        )
                        setItemToEdit(undefined)
                    }}
                    onClose={() => setItemToEdit(undefined)}
                    enableAllFields={enableAllFields}
                />
            )}

            <ConfirmationModal
                isOpen={isShowingPremiumWarningModal}
                onClose={() => setIsShowingPremiumWarningModal(false)}
                title="Premium feature selection"
                size="small"
                confirmButtonText="Dismiss"
                onConfirm={() => {
                    setIsShowingPremiumWarningModal(false)
                }}
            >
                <Paragraph>
                    You selected a premium feature that is not included in the Basic Edition.
                    Premium default items will be hidden until you upgrade to the Premium Edition.
                </Paragraph>

                <div className="mt-10 flex items-center justify-end gap-x-4">
                    <Button
                        onClick={() => setIsShowingPremiumWarningModal(false)}
                        intent="secondary"
                    >
                        Dismiss
                    </Button>
                </div>
            </ConfirmationModal>
        </WorkflowPage>
    )
}
